// Gamification Redux Reducer
import * as actions from './actions';

const initialState = {
    // Profile
    profile: null,
    profileLoading: false,
    profileError: null,

    // XP
    recentXP: [],
    recentXPLoading: false,
    xpSummary: [],
    xpSummaryLoading: false,

    // Achievements
    achievements: [],
    achievementsLoading: false,
    unlockedAchievements: [],
    unlockedAchievementsLoading: false,
    achievementFilter: 'all', // 'all', 'unlocked', 'locked', 'initiation', 'production', etc.

    // Challenges
    challenges: [],
    challengesLoading: false,
    joiningChallenge: false,

    // Leaderboards
    globalLeaderboard: {
        rankings: [],
        userRank: null,
        totalUsers: 0,
    },
    globalLeaderboardLoading: false,
    organizationLeaderboard: {
        organization: null,
        rankings: [],
    },
    organizationLeaderboardLoading: false,
    leaderboardType: 'global_xp',

    // Skill Tree
    skillTree: [],
    skillTreeLoading: false,

    // Notifications
    xpNotification: null,
    levelUpNotification: null,
    achievementNotification: null,

    // General
    error: null,
};

const gamificationReducer = (state = initialState, action) => {
    switch (action.type) {
        // ==================== PROFILE ====================
        case actions.FETCH_GAMIFICATION_PROFILE.REQUEST:
            return {
                ...state,
                profileLoading: true,
                profileError: null,
            };
        case actions.FETCH_GAMIFICATION_PROFILE.SUCCESS:
            return {
                ...state,
                profile: action.payload,
                profileLoading: false,
            };
        case actions.FETCH_GAMIFICATION_PROFILE.FAILURE:
            return {
                ...state,
                profileLoading: false,
                profileError: action.payload,
            };

        case actions.UPDATE_GAMIFICATION_PREFERENCES.REQUEST:
            return {
                ...state,
                profileLoading: true,
            };
        case actions.UPDATE_GAMIFICATION_PREFERENCES.SUCCESS:
            return {
                ...state,
                profile: action.payload,
                profileLoading: false,
            };
        case actions.UPDATE_GAMIFICATION_PREFERENCES.FAILURE:
            return {
                ...state,
                profileLoading: false,
                error: action.payload,
            };

        // ==================== XP ====================
        case actions.FETCH_RECENT_XP.REQUEST:
            return {
                ...state,
                recentXPLoading: true,
            };
        case actions.FETCH_RECENT_XP.SUCCESS:
            return {
                ...state,
                recentXP: action.payload,
                recentXPLoading: false,
            };
        case actions.FETCH_RECENT_XP.FAILURE:
            return {
                ...state,
                recentXPLoading: false,
                error: action.payload,
            };

        case actions.FETCH_XP_SUMMARY.REQUEST:
            return {
                ...state,
                xpSummaryLoading: true,
            };
        case actions.FETCH_XP_SUMMARY.SUCCESS:
            return {
                ...state,
                xpSummary: action.payload,
                xpSummaryLoading: false,
            };
        case actions.FETCH_XP_SUMMARY.FAILURE:
            return {
                ...state,
                xpSummaryLoading: false,
                error: action.payload,
            };

        // ==================== ACHIEVEMENTS ====================
        case actions.FETCH_ACHIEVEMENTS.REQUEST:
            return {
                ...state,
                achievementsLoading: true,
            };
        case actions.FETCH_ACHIEVEMENTS.SUCCESS:
            return {
                ...state,
                achievements: action.payload,
                achievementsLoading: false,
            };
        case actions.FETCH_ACHIEVEMENTS.FAILURE:
            return {
                ...state,
                achievementsLoading: false,
                error: action.payload,
            };

        case actions.FETCH_UNLOCKED_ACHIEVEMENTS.REQUEST:
            return {
                ...state,
                unlockedAchievementsLoading: true,
            };
        case actions.FETCH_UNLOCKED_ACHIEVEMENTS.SUCCESS:
            return {
                ...state,
                unlockedAchievements: action.payload,
                unlockedAchievementsLoading: false,
            };
        case actions.FETCH_UNLOCKED_ACHIEVEMENTS.FAILURE:
            return {
                ...state,
                unlockedAchievementsLoading: false,
                error: action.payload,
            };

        case actions.SET_ACHIEVEMENT_FILTER:
            return {
                ...state,
                achievementFilter: action.payload,
            };

        // ==================== CHALLENGES ====================
        case actions.FETCH_CHALLENGES.REQUEST:
            return {
                ...state,
                challengesLoading: true,
            };
        case actions.FETCH_CHALLENGES.SUCCESS:
            return {
                ...state,
                challenges: action.payload,
                challengesLoading: false,
            };
        case actions.FETCH_CHALLENGES.FAILURE:
            return {
                ...state,
                challengesLoading: false,
                error: action.payload,
            };

        case actions.JOIN_CHALLENGE.REQUEST:
            return {
                ...state,
                joiningChallenge: true,
            };
        case actions.JOIN_CHALLENGE.SUCCESS:
            return {
                ...state,
                joiningChallenge: false,
                // Update the challenge in the list to show as joined
                challenges: state.challenges.map(ch =>
                    ch.id === action.payload.challengeId
                        ? { ...ch, is_joined: true, user_progress: action.payload.progress }
                        : ch
                ),
            };
        case actions.JOIN_CHALLENGE.FAILURE:
            return {
                ...state,
                joiningChallenge: false,
                error: action.payload,
            };

        // ==================== LEADERBOARDS ====================
        case actions.FETCH_GLOBAL_LEADERBOARD.REQUEST:
            return {
                ...state,
                globalLeaderboardLoading: true,
            };
        case actions.FETCH_GLOBAL_LEADERBOARD.SUCCESS:
            return {
                ...state,
                globalLeaderboard: {
                    rankings: action.payload.rankings,
                    userRank: action.payload.user_rank,
                    totalUsers: action.payload.total_users,
                },
                globalLeaderboardLoading: false,
            };
        case actions.FETCH_GLOBAL_LEADERBOARD.FAILURE:
            return {
                ...state,
                globalLeaderboardLoading: false,
                error: action.payload,
            };

        case actions.FETCH_ORGANIZATION_LEADERBOARD.REQUEST:
            return {
                ...state,
                organizationLeaderboardLoading: true,
            };
        case actions.FETCH_ORGANIZATION_LEADERBOARD.SUCCESS:
            return {
                ...state,
                organizationLeaderboard: {
                    organization: action.payload.organization,
                    rankings: action.payload.rankings,
                },
                organizationLeaderboardLoading: false,
            };
        case actions.FETCH_ORGANIZATION_LEADERBOARD.FAILURE:
            return {
                ...state,
                organizationLeaderboardLoading: false,
                error: action.payload,
            };

        case actions.SET_LEADERBOARD_TYPE:
            return {
                ...state,
                leaderboardType: action.payload,
            };

        // ==================== SKILL TREE ====================
        case actions.FETCH_SKILL_TREE.REQUEST:
            return {
                ...state,
                skillTreeLoading: true,
            };
        case actions.FETCH_SKILL_TREE.SUCCESS:
            return {
                ...state,
                skillTree: action.payload,
                skillTreeLoading: false,
            };
        case actions.FETCH_SKILL_TREE.FAILURE:
            return {
                ...state,
                skillTreeLoading: false,
                error: action.payload,
            };

        // ==================== NOTIFICATIONS ====================
        case actions.SHOW_XP_NOTIFICATION:
            return {
                ...state,
                xpNotification: action.payload,
            };

        case actions.SHOW_LEVEL_UP_NOTIFICATION:
            return {
                ...state,
                levelUpNotification: action.payload,
            };

        case actions.SHOW_ACHIEVEMENT_NOTIFICATION:
            return {
                ...state,
                achievementNotification: action.payload,
            };

        case actions.CLEAR_GAMIFICATION_NOTIFICATION:
            return {
                ...state,
                xpNotification: null,
                levelUpNotification: null,
                achievementNotification: null,
            };

        default:
            return state;
    }
};

export default gamificationReducer;
